import { useState, useEffect } from "react";
import axios from "axios";
import ClientAiSummary from "./ClientAiSummary";
import ClientDataTools from "./ClientDataTools";
import ClientLanguage from "./ClientLanguage";
import { API } from "@/App";
import { useTranslation } from "@/i18n/translations";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { ChevronDown, ChevronUp, Phone, User } from "lucide-react";

const STATUS_COLORS = {
  pending: "bg-yellow-100 text-yellow-800",
  confirmed: "bg-green-100 text-green-800",
  cancelled: "bg-red-100 text-red-800",
};

const CHANNEL_CLS = {
  instagram: "bg-pink-100 text-pink-700 border-pink-300",
  whatsapp: "bg-green-100 text-green-700 border-green-300",
  messenger: "bg-blue-100 text-blue-700 border-blue-300",
  twilio_call: "bg-purple-100 text-purple-700 border-purple-300",
  phone: "bg-purple-100 text-purple-700 border-purple-300",
  widget: "bg-gray-100 text-gray-700 border-gray-300",
};
const CHANNEL_ICON = { instagram: "📷", whatsapp: "📲", messenger: "💬", twilio_call: "📞", phone: "📞", widget: "🌐" };

export const ClientsList = () => {
  const { t } = useTranslation();
  const [clients, setClients] = useState([]);
  const [fullHistory, setFullHistory] = useState(true);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState(null);

  const load = async () => {
    try {
      const res = await axios.get(`${API}/clients`);
      setClients(res.data.clients || []);
      setFullHistory(res.data.full_history);
    } catch (e) {
      console.error("Clients fetch error:", e);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const filtered = clients.filter((c) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      (c.client_name || "").toLowerCase().includes(q) ||
      c.phones.some((p) => p.replace(/\D/g, "").includes(q.replace(/\D/g, "")) && q.replace(/\D/g, ""))
    );
  });

  const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString() : "—");

  return (
    <Card className="bg-white text-black border-2 md:border-4 border-black">
      <CardHeader>
        <CardTitle className="text-2xl">👥 {t("clients.title") || "База клиентов"}</CardTitle>
        <CardDescription className="text-gray-600">
          {t("clients.subtitle") || "Заявки автоматически объединены по номеру телефона (разные форматы номера склеиваются)"}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {!fullHistory && (
          <div
            data-testid="clients-limit-note"
            className="mb-4 p-3 bg-amber-50 border-2 border-amber-400 rounded-lg text-sm font-medium"
          >
            🔒 {t("clients.limitedNote") || "На тарифе Starter показаны клиенты за последние 30 дней. Полная история — на Professional"}
          </div>
        )}

        <Input
          data-testid="clients-search-input"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t("clients.search") || "Поиск по имени или телефону"}
          className="mb-4 border-2 border-black"
        />

        {loading ? (
          <div className="text-center py-12 text-gray-500">...</div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-12 text-gray-500" data-testid="clients-empty">
            {t("clients.noClients") || "Пока нет клиентов"}
          </div>
        ) : (
          <div className="space-y-3">
            {filtered.map((c) => (
              <div key={c.phone_key} className="border-2 border-black rounded-lg bg-white overflow-hidden">
                <button
                  data-testid={`client-card-${c.phone_key}`}
                  onClick={() => setExpanded(expanded === c.phone_key ? null : c.phone_key)}
                  className="w-full p-4 flex items-center justify-between gap-3 text-left hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-10 h-10 rounded-full bg-black text-white flex items-center justify-center shrink-0">
                      <User size={18} />
                    </div>
                    <div className="min-w-0">
                      <p className="font-bold truncate">{c.client_name || "—"}</p>
                      <p className="text-sm text-gray-600 flex items-center gap-1">
                        <Phone size={12} /> {c.phones[0]}
                        {c.phones.length > 1 && (
                          <span className="text-xs text-gray-400">+{c.phones.length - 1}</span>
                        )}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 shrink-0">
                    <div className="text-right hidden sm:block">
                      <p className="text-sm font-bold">{c.visits} {t("clients.visits") || "заявок"}</p>
                      <p className="text-xs text-gray-500">
                        {t("clients.lastVisit") || "Последний визит"}: {fmtDate(c.last_visit)}
                      </p>
                    </div>
                    <span className="px-2 py-1 bg-gray-100 rounded-full text-sm font-bold sm:hidden">{c.visits}</span>
                    {expanded === c.phone_key ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                  </div>
                </button>

                {expanded === c.phone_key && (
                  <div className="px-4 pb-4 border-t-2 border-gray-100" data-testid={`client-history-${c.phone_key}`}>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 py-3 text-sm">
                      <div>
                        <p className="text-gray-500 text-xs">{t("clients.firstVisit") || "Первый визит"}</p>
                        <p className="font-medium">{fmtDate(c.first_visit)}</p>
                      </div>
                      <div>
                        <p className="text-gray-500 text-xs">{t("clients.lastVisit") || "Последний визит"}</p>
                        <p className="font-medium">{fmtDate(c.last_visit)}</p>
                      </div>
                      <div className="col-span-2">
                        <p className="text-gray-500 text-xs">{t("clients.phones") || "Все номера"}</p>
                        <p className="font-medium">{c.phones.join(", ")}</p>
                      </div>
                      {c.services.length > 0 && (
                        <div className="col-span-2">
                          <p className="text-gray-500 text-xs">{t("clients.services") || "Услуги"}</p>
                          <p className="font-medium">{c.services.join(", ")}</p>
                        </div>
                      )}
                      {c.sources.length > 0 && (
                        <div className="col-span-2">
                          <p className="text-gray-500 text-xs">{t("clients.source") || "Источник"}</p>
                          <p className="font-medium">{c.sources.join(", ")}</p>
                        </div>
                      )}
                      <ClientLanguage client={c} t={t} onChanged={(v) => setClients((prev) => prev.map((x) => x.phone_key === c.phone_key ? { ...x, language: v } : x))} />
                    </div>
                    <ClientAiSummary phoneKey={c.phone_key} />
                    <ClientDataTools client={c} onChanged={(kind) => { if (kind === "deleted") setExpanded(null); load(); }} />
                    <p className="font-bold text-sm mb-2">📋 {t("clients.history") || "История заявок"}</p>
                    <div className="space-y-2">
                      {c.history.map((h, i) => (
                        <div key={`${c.phone_key}-h-${h.date}-${i}`} className="flex items-center justify-between gap-2 p-2 bg-gray-50 rounded text-sm">
                          <div>
                            <span className="font-medium">{fmtDate(h.date)}</span>
                            {h.service && <span className="text-gray-600"> · {h.service}</span>}
                            {h.preferred_date && <span className="text-gray-400"> → {h.preferred_date}</span>}
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            {h.channel && (
                              <span className={`text-xs px-2 py-0.5 border rounded-full ${CHANNEL_CLS[h.channel] || CHANNEL_CLS.widget}`}>
                                {CHANNEL_ICON[h.channel] || "🌐"} {h.channel}
                              </span>
                            )}
                            {h.utm_source && h.utm_source !== h.channel && (
                              <span className="text-xs px-2 py-0.5 bg-blue-50 text-blue-700 rounded-full">{h.utm_source}</span>
                            )}
                            <span className={`text-xs px-2 py-0.5 rounded-full ${STATUS_COLORS[h.status] || "bg-gray-100 text-gray-700"}`}>
                              {h.status}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ClientsList;
